"use client";

/**
 * Inline plain-text editor for the canvas.
 * Renders a static text element that becomes contentEditable on click (builder mode only).
 *
 * This is now a thin, store-bound wrapper around the shared, headless
 * `InlineEditableText`/`useContentEditableField` primitive in `@/components/ui/inline-edit` — it
 * supplies Landing Page v2's builder-store edit state (`useInlineEdit`) and public-view behavior,
 * and delegates all contentEditable mechanics (focus, caret, commit, cancel, plain-text paste,
 * keyboard entry) to the primitive.
 */

import type React from "react";
import { useCallback } from "react";
import { InlineEditableText } from "@/components/ui/inline-edit/InlineEditableText";
import { useInlineEdit } from "../../../../_hooks/useInlineEdit";

interface InlineTextEditProps {
  /** HTML tag to render */
  tag?: "h1" | "h2" | "h3" | "span" | "div" | "p";
  /** Current text value */
  value: string;
  /** Placeholder when value is empty */
  placeholder?: string;
  /** Tailwind classes for the element */
  className?: string;
  /** Component ID that owns this field */
  componentId: string;
  /** Field ID for tracking inline edit state (e.g., "hero-title") */
  fieldId: string;
  /** Component property name to update (e.g., "title", "date"). Required when onCommit is not provided. */
  field?: string;
  /** Custom commit handler. When provided, called instead of updateComponent. */
  onCommit?: (value: string) => void;
}

/**
 * Plain-text inline editor.
 * Click to enter edit mode, blur/Enter to commit, Escape to cancel.
 */
export function InlineTextEdit({
  tag = "div",
  value,
  placeholder,
  className,
  componentId,
  fieldId,
  field,
  onCommit,
}: InlineTextEditProps): React.ReactElement {
  // Hooks
  const { startEditing, stopEditing, isEditing, commitEdit, isPublic } = useInlineEdit(componentId);

  const handleCommitValue = useCallback(
    (text: string) => {
      if (onCommit) {
        onCommit(text);
        stopEditing();
      } else if (field) {
        commitEdit(field, text);
      }
    },
    [onCommit, field, commitEdit, stopEditing]
  );

  const handleRequestEdit = useCallback(() => startEditing(fieldId), [startEditing, fieldId]);

  // Public view keeps its original behavior: render nothing for an empty value. A non-empty value
  // renders as static text via the primitive's read-only (`disabled`) mode — identical markup.
  if (isPublic && !value) return <></>;

  return (
    <InlineEditableText
      tag={tag}
      value={value}
      placeholder={placeholder}
      className={className}
      isEditing={isEditing(fieldId)}
      disabled={isPublic}
      onCommit={handleCommitValue}
      onCancel={stopEditing}
      onRequestEdit={handleRequestEdit}
    />
  );
}
